// BillingView.jsx — View PURA da /billing ("Compras"). Recebe { vm, actions }.
// Contrato: billing.vm.js (@version 2). Sem fetch, sem conta, sem formatação: todo rótulo, preço,
// prazo e derivado (lastsLabel, savingsLabel, runwayLabel) chega pronto do vm. NÃO há `useState`
// aqui — esta tela não tem estado de UI próprio (nenhum menu, aba ou drawer); hover e foco são CSS.
// O checkout PIX e o diálogo de CPF não são desta View: `onBuyPackage(id)` e acabou.
//
// ═══════════════════════════════════════════════════════════════════════════════
// A DECISÃO DE DESENHO: TRÊS FAIXAS DE LEITURA, UMA HISTÓRIA SÓ
//
// A pessoa chega chateada, vinda de um push: "mensagem não enviada, sem créditos". A tela tem que
// fazer três coisas nessa ordem e sem fricção — reconhecer, explicar, consertar:
//
//   1. RECONHECIMENTO (faixa larga, topo) — só com `arrivedFromAlert`. Diz o que aconteceu, assume
//      a parte que é nossa (a gente não avisava) e emenda no conserto. Sem isto, a tela finge que
//      a visita foi espontânea e desperdiça o único contexto que ela tem.
//   2. O ESTADO DA CONTA (coluna esquerda) — saldo com TEMPERATURA (o `level` muda o fundo, a
//      borda, o ícone e a frase, não só a cor de um chip) e o RITMO (os 30 dias reais em barras).
//      O ritmo é o fio: "era assim que você usava" → e a linha do saldo abaixo dele fecha a frase.
//   3. A LOJA (coluna direita, a maior) — PIX pendente quando existe, depois o catálogo.
//
// POR QUE O CATÁLOGO NÃO SÃO TRÊS CAIXINHAS: o que decide não é o preço, é o `lastsLabel`
// ("dura cerca de 2 meses"). Então cada pacote é uma LINHA com uma BARRA proporcional à
// quantidade (100 é 5% de 2.000 — e isso se vê), a duração em destaque e o preço na coluna fixa
// da direita. A escala aparece; três cards iguais escondem justamente a comparação. Com um pacote
// só (mockUmPacote) a barra some — proporção de um item é enfeite — e a linha vira ficha única.
//
// ORDEM NO CELULAR = ORDEM DO DOM: reconhecimento → saldo → LOJA → ritmo. Quem chega por push no
// telefone tem pressa: o gráfico não pode ficar entre a pessoa e o botão de comprar. No desktop a
// mesma ordem de DOM é remontada em duas colunas por `gridTemplateAreas`.
//
// ERRO E VAZIO SÃO DA LOJA, NÃO DA TELA: em `mockErro`/`mockSemPacotes` o saldo e o ritmo têm dado
// bom e continuam de pé — só o catálogo cai. Catálogo vazio é falha NOSSA e o texto assume isso.
//
// Consistência: mesmo CARD_SX (raio, borda, sombra) de Serviços / Ausências / Páginas / Domínio,
// mesmos chips de tint, mesmo StateBlock, mesmo par de registros de chip. Neutros saem de
// --bg-page/--divider/--text-* (papéis themeáveis) e NUNCA da escala --slate-*, que é global fixa
// e clara (quebraria os temas de superfície escura).
import {
  Box, Typography, Button, Skeleton, Tooltip,
} from '@mui/material';
import ShoppingCartRounded from '@mui/icons-material/ShoppingCartRounded';
import NotificationsActiveRounded from '@mui/icons-material/NotificationsActiveRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import BoltRounded from '@mui/icons-material/BoltRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import TimerOffRounded from '@mui/icons-material/TimerOffRounded';
import HourglassTopRounded from '@mui/icons-material/HourglassTopRounded';
import ShowChartRounded from '@mui/icons-material/ShowChartRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import QrCode2Rounded from '@mui/icons-material/QrCode2Rounded';
import SellRounded from '@mui/icons-material/SellRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import ChatBubbleRounded from '@mui/icons-material/ChatBubbleRounded';
import PowerSettingsNewRounded from '@mui/icons-material/PowerSettingsNewRounded';
import CloudOffRounded from '@mui/icons-material/CloudOffRounded';

/* ═══════════════════════════════════════════════════════════════════════════
   Tokens locais (mesmos papéis das outras telas)
   ═══════════════════════════════════════════════════════════════════════════ */

const CARD_SX = {
  bgcolor: 'var(--surface)',
  border: '1px solid',
  borderColor: 'var(--divider)',
  borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-card)',
};

const TINT = {
  success: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', fg: 'var(--tint-success-fg)', sub: 'var(--tint-success-sub)', main: 'var(--color-success)' },
  warning: { bg: 'var(--tint-warning-bg)', border: 'var(--tint-warning-border)', fg: 'var(--tint-warning-fg)', sub: 'var(--tint-warning-sub)', main: 'var(--color-warning)' },
  error: { bg: 'var(--tint-error-bg)', border: 'var(--tint-error-border)', fg: 'var(--tint-error-fg)', sub: 'var(--tint-error-sub)', main: 'var(--color-error)' },
  info: { bg: 'var(--tint-info-bg)', border: 'var(--tint-info-border)', fg: 'var(--tint-info-fg)', sub: 'var(--tint-info-sub)', main: 'var(--color-info)' },
  neutral: { bg: 'var(--bg-page)', border: 'var(--divider)', fg: 'var(--text-secondary)', sub: 'var(--text-secondary)', main: 'var(--text-secondary)' },
  brand: { bg: 'var(--color-primary-soft)', border: 'var(--color-primary-soft)', fg: 'var(--color-primary-dark)', sub: 'var(--color-primary-dark)', main: 'var(--color-primary)' },
};

const tint = (tone) => TINT[tone] || TINT.neutral;

// CTA COMERCIAL — o interesse do clique é NOSSO (comprar créditos, terminar o PIX). Não herda
// --color-primary de propósito: a marca é por nicho (vinho/vermelha em Salão e Tatuagem) e
// vermelho em botão de compra lê "pare". Fallback no verde ESCURO que já existe
// (--tint-success-fg): --color-success é cor de texto/ícone e como preenchimento reprova o
// contraste com --on-primary. Tudo o mais da vitrine é derivado por color-mix com --surface,
// então inverte sozinho em tema de superfície escura (Barbearia, Tatuagem).
const CTA_FILL = 'var(--color-cta, var(--tint-success-fg))';
const CTA_FILL_DARK = 'var(--color-cta-dark, color-mix(in srgb, var(--color-cta, var(--tint-success-fg)) 86%, var(--text-primary)))';
const CTA_SOFT = `color-mix(in srgb, ${CTA_FILL} 12%, var(--surface))`;
const CTA_EDGE = `color-mix(in srgb, ${CTA_FILL} 34%, var(--surface))`;
const CTA_INK = `color-mix(in srgb, ${CTA_FILL} 86%, var(--text-primary))`;

// Peso de título na fonte de CORPO (h3–h6 já vêm na fonte fixa do painel; só h1/h2 levam a fonte
// do nicho). Número de saldo e preço NUNCA em h1/h2 — display condensada em "R$ 259,90" é ruim.
const HEAD = { fontWeight: 'var(--fw-bold)' };
const BREAK = { overflowWrap: 'anywhere', lineHeight: 1.35 };

// ⚠️ ÚNICA exceção de tamanho nesta tela, e ela é RESPONSIVA (o veto é a `fontSize` crua, que
// estoura no celular): o saldo é a manchete da tela e a escala para em --fs-h1 (1,75rem). No
// celular ele cai para o token da escala; do `sm` para cima ganha o tamanho de manchete.
// Mesmo precedente do preço na vitrine de Domínio próprio.
const DISPLAY_SX = { fontWeight: 'var(--fw-bold)', lineHeight: 1.1, fontSize: { xs: 'var(--fs-h1)', sm: '2.5rem' } };

const CTA_BTN_SX = {
  bgcolor: CTA_FILL, color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.5, minHeight: 46,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': { bgcolor: CTA_FILL_DARK, boxShadow: 'none' },
  '&:focus-visible': { outline: `2px solid ${CTA_FILL_DARK}`, outlineOffset: 2 },
};

// Comprar quando JÁ existe um PIX em aberto: o catálogo continua inteiro, mas em segundo plano —
// só o "continuar o pagamento" fica preenchido, para não haver dois botões de mesmo peso.
const CTA_OUTLINE_BTN_SX = {
  minHeight: 46, px: 2.5, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  border: '1px solid', borderColor: CTA_EDGE, bgcolor: 'var(--surface)',
  color: CTA_INK, fontWeight: 'var(--fw-bold)', whiteSpace: 'nowrap',
  '&:hover': { borderColor: CTA_FILL, bgcolor: CTA_SOFT },
  '&:focus-visible': { outline: `2px solid ${CTA_FILL_DARK}`, outlineOffset: 2 },
};

// Tarefa do usuário (tentar de novo, falar com o suporte) — cor de marca, não CTA comercial.
const PRIMARY_BTN_SX = {
  bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.25, minHeight: 44,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': { bgcolor: 'var(--color-primary-dark)', boxShadow: 'none' },
  '&:focus-visible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 2 },
};

const QUIET_BTN_SX = {
  minHeight: 44, px: 1.5, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)',
  '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--bg-page)' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
};

/* Temperatura do saldo: o `level` muda a tela, não só um chip. */
const LEVEL = {
  zero: { tone: 'error', Icon: ErrorRounded, line: 'Nenhuma mensagem sai até você recarregar.' },
  low: { tone: 'warning', Icon: WarningAmberRounded, line: 'Ainda dá para enviar, mas não por muito tempo.' },
  ok: { tone: 'success', Icon: CheckCircleRounded, line: 'Suas mensagens estão saindo normalmente.' },
};

/** Entrada escalonada: o olho segue a ordem da leitura em vez de receber tudo de uma vez. */
const rise = (delay = 0) => ({
  '@keyframes bilRise': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'none' },
  },
  animation: 'bilRise .5s cubic-bezier(.2,.7,.3,1) both',
  animationDelay: `${delay}ms`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/* ═══════════════════════════════════════════════════════════════════════════
   Peças pequenas (mesma linguagem das telas irmãs)
   ═══════════════════════════════════════════════════════════════════════════ */

/** Chip de estado — DOIS REGISTROS, um critério: o estado PEDE AÇÃO ou não.
 *   · error / warning  → pastilha preenchida (tint + fio + ícone): salta na página;
 *   · success / info / neutral → PONTO + rótulo, sem pastilha: a leitura fica calma.
 *  Estado nunca só por cor: o rótulo nomeia o estado nos dois registros. */
const CHIP_FILLED = { error: true, warning: true };

function ToneChip({ tone = 'neutral', icon, label }) {
  const t = tint(tone);
  if (!CHIP_FILLED[tone]) return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, maxWidth: '100%', minWidth: 0 }}>
      <Box aria-hidden="true" component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.fg, flexShrink: 0 }} />
      <Typography variant="caption" sx={{ color: tone === 'neutral' ? 'var(--text-secondary)' : 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>
        {label}
      </Typography>
    </Box>
  );
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.625, maxWidth: '100%',
        bgcolor: t.bg, border: '1px solid', borderColor: t.border,
        borderRadius: 999, px: 1.125, py: '3px',
      }}
    >
      {icon && <Box component="span" sx={{ display: 'flex', color: t.fg }}>{icon}</Box>}
      <Typography variant="caption" sx={{ color: t.fg, fontWeight: 'var(--fw-medium)' }}>{label}</Typography>
    </Box>
  );
}

/** Chip da vitrine (tom de ação, não de marca): desconto, sugestão. */
function CtaChip({ icon, label }) {
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.625, maxWidth: '100%',
        bgcolor: CTA_SOFT, border: '1px solid', borderColor: CTA_EDGE,
        borderRadius: 999, px: 1.125, py: '3px',
      }}
    >
      {icon && <Box component="span" sx={{ display: 'flex', color: CTA_INK }}>{icon}</Box>}
      <Typography variant="caption" sx={{ color: CTA_INK, fontWeight: 'var(--fw-bold)' }}>{label}</Typography>
    </Box>
  );
}

/** Linha de aviso dentro de um bloco (tint + ícone + texto pronto do VM). */
function NoteRow({ tone = 'info', icon, children, sx }) {
  const t = tint(tone);
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1,
        bgcolor: t.bg, border: '1px solid', borderColor: t.border,
        borderRadius: 'var(--radius-sm)', px: 1.5, py: 1.25, ...sx,
      }}
    >
      {icon && <Box component="span" sx={{ display: 'flex', color: t.fg, mt: '1px', flexShrink: 0 }}>{icon}</Box>}
      <Box sx={{ minWidth: 0, color: t.fg }}>{children}</Box>
    </Box>
  );
}

function CardTitle({ icon, children, hint }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      {icon && <Box component="span" sx={{ display: 'flex', color: 'var(--text-secondary)', mt: '2px' }}>{icon}</Box>}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)' }}>
          {children}
        </Typography>
        {hint && (
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)', mt: '2px', lineHeight: 1.45 }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/** Bloco de estado (erro / catálogo vazio) — mesmo desenho das telas irmãs. */
function StateBlock({ icon, title, description, action }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: { xs: 3, sm: 4 }, px: 2 }}>
      <Box
        sx={{
          width: 64, height: 64, borderRadius: '50%', mb: 2, bgcolor: 'var(--bg-page)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle1" component="h3" sx={{ ...HEAD, color: 'var(--text-primary)', mb: 0.5, textWrap: 'pretty' }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 420, lineHeight: 1.5, textWrap: 'pretty' }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   1. RECONHECIMENTO — a faixa de continuidade entre "deu ruim" e "resolvido"
   Só com `arrivedFromAlert`. A pessoa tocou num aviso; fingir que a visita foi espontânea é
   desperdiçar o contexto. O tom é "a gente resolvendo junto": a parte que é nossa (não avisar
   antes) está escrita, sem cobrança e sem "você errou".
   ═══════════════════════════════════════════════════════════════════════════ */

function AlertAck({ vm }) {
  const zero = vm.credits?.level === 'zero';
  const tone = zero ? 'error' : 'warning';
  const t = tint(tone);
  const hasLosses = !!vm.loss?.hasLosses;

  return (
    <Box
      sx={{
        gridArea: 'ack',
        ...CARD_SX,
        bgcolor: t.bg, borderColor: t.border,
        p: { xs: 2, sm: 2.5 },
        display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2 },
        ...rise(0),
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: 44, height: 44, flexShrink: 0, borderRadius: '50%',
          bgcolor: 'var(--surface)', border: '1px solid', borderColor: t.border,
          display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center',
          '@keyframes bilRing': { '0%, 100%': { transform: 'rotate(0deg)' }, '10%': { transform: 'rotate(-12deg)' }, '20%': { transform: 'rotate(10deg)' }, '30%': { transform: 'rotate(-6deg)' }, '40%': { transform: 'rotate(0deg)' } },
          animation: 'bilRing 4.5s ease-in-out infinite',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        <NotificationsActiveRounded sx={{ fontSize: 24, color: t.fg }} />
      </Box>

      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="overline" sx={{ color: t.sub, ...HEAD, letterSpacing: '.08em' }}>
          Você veio do aviso
        </Typography>
        <Typography variant="h5" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', lineHeight: 1.25, textWrap: 'pretty' }}>
          {hasLosses
            ? 'Seu cliente não foi avisado do agendamento porque o saldo acabou.'
            : 'Seu saldo de mensagens chegou ao limite.'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--text-primary)', lineHeight: 1.5, maxWidth: 720, textWrap: 'pretty' }}>
          Parte disso é nossa: a gente devia ter avisado antes de o saldo chegar a zero, e não
          avisou. Agora avisa — e o conserto está logo abaixo. Assim que o PIX cair, o envio
          volta sozinho, sem você reconfigurar nada.
        </Typography>
        {hasLosses && vm.loss?.totalLostLabel && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {/* ⚠️ total HISTÓRICO da conta — nada de "hoje" nem "esta semana" em cima deste número. */}
            <ToneChip tone={tone} icon={<ChatBubbleRounded sx={{ fontSize: 13 }} />} label={vm.loss.totalLostLabel} />
            <Typography variant="caption" sx={{ color: t.sub }}>no total desta conta</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   2a. O SALDO — estado com temperatura, não número solto
   `loading` NUNCA mostra "0 créditos": dizer que a pessoa quebrou quando o dado ainda não
   chegou é o pior erro possível desta tela.
   ═══════════════════════════════════════════════════════════════════════════ */

function BalanceCard({ vm }) {
  const c = vm.credits || {};
  const lv = LEVEL[c.level] || LEVEL.ok;
  const t = tint(lv.tone);
  const { Icon } = lv;
  // O saldo em bom estado é uma informação calma: superfície normal. Zero e baixo tomam a cor
  // do estado no cartão inteiro — é o que faz a temperatura mudar a tela, e não só um chip.
  const alarmed = c.level === 'zero' || c.level === 'low';
  const showLossHere = !!vm.loss?.hasLosses && !vm.arrivedFromAlert;

  return (
    <Box
      sx={{
        gridArea: 'saldo', alignSelf: 'start',
        ...CARD_SX,
        bgcolor: alarmed ? t.bg : 'var(--surface)',
        borderColor: alarmed ? t.border : 'var(--divider)',
        overflow: 'hidden',
        ...rise(60),
      }}
    >
      {/* filete de temperatura: some no estado calmo */}
      {alarmed && <Box aria-hidden="true" sx={{ height: 4, bgcolor: t.main }} />}

      <Box sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="overline" sx={{ color: 'var(--text-secondary)', ...HEAD, letterSpacing: '.08em' }}>
          Saldo de mensagens
        </Typography>

        {c.loading ? (
          <Box aria-live="polite">
            <Skeleton variant="rounded" width="62%" height={44} sx={{ bgcolor: 'var(--bg-page)' }} />
            <Skeleton variant="text" width="80%" sx={{ bgcolor: 'var(--bg-page)', mt: 1 }} />
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', mt: 0.5 }}>
              Conferindo seu saldo…
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Icon aria-hidden="true" sx={{ fontSize: 30, color: t.fg, flexShrink: 0 }} />
              <Typography
                variant="h4"
                component="p"
                sx={{ ...DISPLAY_SX, color: alarmed ? t.fg : 'var(--text-primary)', ...BREAK, lineHeight: 1.1 }}
              >
                {c.balanceLabel}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: alarmed ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: alarmed ? 'var(--fw-medium)' : 'var(--fw-regular)', lineHeight: 1.45, textWrap: 'pretty' }}>
              {lv.line}
            </Typography>
          </Box>
        )}

        {!c.loading && vm.usage?.runwayLabel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ScheduleRounded aria-hidden="true" sx={{ fontSize: 16, color: 'var(--text-secondary)', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', ...BREAK }}>
              {vm.usage.runwayLabel}
            </Typography>
          </Box>
        )}

        {/* Dentro de um cartão já tintado (zero/baixo), a nota volta para --surface: nota de tint
            sobre fundo do mesmo tint vira um bloco chapado onde só a borda aparece. */}
        {showLossHere && vm.loss?.totalLostLabel && (
          <NoteRow tone="error" icon={<ChatBubbleRounded sx={{ fontSize: 18 }} />} sx={alarmed ? { bgcolor: 'var(--surface)' } : undefined}>
            <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
              {vm.loss.totalLostLabel} — no total desta conta.
            </Typography>
          </NoteRow>
        )}

        {vm.expiringLabel && (
          <NoteRow tone="warning" icon={<TimerOffRounded sx={{ fontSize: 18 }} />} sx={alarmed ? { bgcolor: 'var(--surface)' } : undefined}>
            <Typography variant="body2" sx={{ lineHeight: 1.45 }}>{vm.expiringLabel}</Typography>
          </NoteRow>
        )}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   2b. O RITMO — 30 dias reais em barras
   A série é irregular de propósito (fim de semana fraco, pico no meio, zeros). Dia zerado vira
   um toco na linha de base, e não um buraco: o vazio faz parte do ritmo. Nada de anotar "parou
   aqui" no gráfico — a View não sabe em que dia o saldo acabou; quem diz isso é o `runwayLabel`,
   em texto, logo acima. Sem `hasData` não existe gráfico: linha reta em zero mente.
   ═══════════════════════════════════════════════════════════════════════════ */

const BAR = `color-mix(in srgb, var(--color-primary) 62%, var(--surface))`;
const BAR_PEAK = 'var(--color-primary)';

function UsageChart({ usage }) {
  const days = usage.days || [];
  const max = usage.maxConsumed || 1;
  return (
    <Box
      role="img"
      aria-label={`Consumo diário dos últimos ${days.length} dias. Pico de ${usage.maxConsumed} mensagens em um dia.`}
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
    >
      {/* mais alto no desktop: a coluna de estado é mais curta que a loja, e a série de 30 dias é
          o conteúdo que merece esse espaço em vez de ficar sobrando embaixo. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: { xs: 92, sm: 116, lg: 180 } }}>
        {days.map((d, i) => {
          const isPeak = d.consumed > 0 && d.consumed === usage.maxConsumed;
          return (
            <Tooltip
              key={d.dateLabel}
              title={`${d.dateLabel} · ${d.consumed} ${d.consumed === 1 ? 'mensagem' : 'mensagens'}`}
              placement="top"
              arrow
            >
              <Box
                sx={{
                  flex: 1, minWidth: 0, height: '100%',
                  display: 'flex', alignItems: 'flex-end', cursor: 'default',
                  '&:hover > span': { bgcolor: BAR_PEAK },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'block', width: '100%',
                    height: d.consumed > 0 ? `${Math.max(6, (d.consumed / max) * 100)}%` : '3px',
                    bgcolor: d.consumed > 0 ? (isPeak ? BAR_PEAK : BAR) : 'var(--divider)',
                    borderRadius: '3px 3px 0 0',
                    transition: 'background-color .15s',
                    transformOrigin: 'bottom',
                    '@keyframes bilGrow': { from: { transform: 'scaleY(0)' }, to: { transform: 'scaleY(1)' } },
                    animation: 'bilGrow .45s cubic-bezier(.2,.7,.3,1) both',
                    animationDelay: `${120 + i * 16}ms`,
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ borderTop: '1px solid', borderColor: 'var(--divider)', pt: 0.5, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{days[0]?.dateLabel}</Typography>
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{days[days.length - 1]?.dateLabel}</Typography>
      </Box>
    </Box>
  );
}

function UsageCard({ vm, loading }) {
  const usage = vm.usage || {};
  const days = usage.days || [];

  return (
    <Box sx={{ gridArea: 'ritmo', alignSelf: 'start', ...CARD_SX, p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2, ...rise(120) }}>
      <CardTitle icon={<ShowChartRounded sx={{ fontSize: 20 }} />} hint={usage.hasData ? 'Mensagens enviadas por dia — é este ritmo que dimensiona o pacote.' : null}>
        Seu ritmo de envio
      </CardTitle>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: { xs: 92, sm: 116, lg: 180 } }}>
          {Array.from({ length: 30 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              sx={{
                flex: 1, bgcolor: 'var(--bg-page)', borderRadius: '3px 3px 0 0',
                height: `${34 + ((i * 37) % 62)}%`,
              }}
            />
          ))}
        </Box>
      ) : usage.hasData && days.length > 0 ? (
        <>
          <UsageChart usage={usage} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, alignItems: 'center' }}>
            {usage.perWeekLabel && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
                <BoltRounded aria-hidden="true" sx={{ fontSize: 16, color: 'var(--color-primary-dark)' }} />
                <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>
                  {usage.perWeekLabel}
                </Typography>
              </Box>
            )}
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
              {usage.totalConsumed} mensagens nos últimos {days.length} dias
            </Typography>
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, bgcolor: 'var(--bg-page)', border: '1px dashed', borderColor: 'var(--divider)', borderRadius: 'var(--radius-sm)', p: 1.75 }}>
          <HourglassTopRounded aria-hidden="true" sx={{ fontSize: 20, color: 'var(--text-secondary)', flexShrink: 0, mt: '1px' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)', lineHeight: 1.45 }}>
              Ainda não dá para medir seu ritmo.
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', lineHeight: 1.45 }}>
              Depois de alguns dias enviando, aparece aqui quanto você usa por semana — e quanto
              cada pacote dura no seu caso.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   3a. PIX EM ABERTO — a conversão mais barata da tela
   A pessoa já escolheu o tamanho e já gerou o código; só não terminou. Ganha bloco próprio, no
   topo da loja, com o único botão preenchido da coluna — mas o catálogo continua inteiro logo
   abaixo, porque ela pode ter mudado de ideia sobre o tamanho.
   ═══════════════════════════════════════════════════════════════════════════ */

function PendingPurchase({ p, a }) {
  return (
    <Box
      sx={{
        borderRadius: 'var(--radius-card)', p: { xs: 1.75, sm: 2.25 },
        bgcolor: CTA_SOFT, border: '1px solid', borderColor: CTA_EDGE,
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1.5, sm: 2 },
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ minWidth: 0, flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 8, height: 8, borderRadius: '50%', bgcolor: CTA_FILL, flexShrink: 0,
              '@keyframes bilPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
              animation: 'bilPulse 1.8s ease-in-out infinite',
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          />
          <Typography variant="overline" sx={{ color: CTA_INK, ...HEAD, letterSpacing: '.08em' }}>
            Pagamento começado
          </Typography>
        </Box>
        <Typography variant="h6" component="p" sx={{ ...HEAD, color: 'var(--text-primary)', ...BREAK }}>
          {p.packLabel} · {p.priceLabel}
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--text-primary)', lineHeight: 1.45, textWrap: 'pretty' }}>
          Você gerou o PIX de {p.qtyLabel} {p.agoLabel} e ele ainda não foi pago. Terminar leva
          poucos segundos — os créditos entram assim que o pagamento cair.
        </Typography>
      </Box>

      <Box sx={{ flex: '0 0 auto', width: { xs: '100%', sm: 'auto' } }}>
        <Button
          onClick={() => a.onResumePurchase?.()}
          disableElevation
          startIcon={<QrCode2Rounded sx={{ fontSize: 20 }} />}
          sx={{ ...CTA_BTN_SX, width: { xs: '100%', sm: 'auto' }, minHeight: 50 }}
        >
          Continuar o pagamento
        </Button>
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   3b. O CATÁLOGO — linhas com escala, não três caixinhas
   ═══════════════════════════════════════════════════════════════════════════ */

function PackageRow({ pkg, maxQty, showBar, secondary, a, delay }) {
  const featured = !!pkg.highlighted && !secondary;
  const pct = maxQty > 0 ? Math.max(3, (pkg.qty / maxQty) * 100) : 100;

  return (
    <Box
      component="li"
      sx={{
        listStyle: 'none',
        borderRadius: 'var(--radius-card)',
        border: featured ? '2px solid' : '1px solid',
        borderColor: featured ? CTA_EDGE : 'var(--divider)',
        bgcolor: featured ? CTA_SOFT : 'var(--surface)',
        p: { xs: 1.75, sm: 2 },
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1.5, sm: 2 },
        transition: 'transform .18s ease, border-color .18s ease, box-shadow .18s ease',
        '&:hover': { transform: 'translateY(-2px)', borderColor: CTA_EDGE, boxShadow: 'var(--shadow-card)' },
        '@media (prefers-reduced-motion: reduce)': { '&:hover': { transform: 'none' } },
        ...rise(delay),
      }}
    >
      {/* o que é o pacote, quanto dura, e a barra que dá a proporção.
          Base 200px (e não 240): com a coluna de preço em 190 + 16 de gap, a linha ainda cabe
          inteira na coluna estreita de lg (pane de ~876px em 1200 de viewport) em vez de quebrar
          e deixar o preço solto no meio da linha de baixo. */}
      <Box sx={{ flex: '1 1 200px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="h6" component="h3" sx={{ ...HEAD, color: 'var(--text-primary)', ...BREAK }}>
            {pkg.qtyLabel}
          </Typography>
          {featured && <CtaChip icon={<StarRounded sx={{ fontSize: 13 }} />} label="Nossa sugestão" />}
          {pkg.savingsLabel && <CtaChip icon={<SellRounded sx={{ fontSize: 13 }} />} label={pkg.savingsLabel} />}
        </Box>

        {/* A barra vem COLADA no título porque é o título que ela anota: é o tamanho do pacote em
            escala (100 é 5% de 2.000, e isso se vê de longe). Some com um pacote só. */}
        {showBar && (
          <Box aria-hidden="true" sx={{ height: 8, borderRadius: 999, bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%', width: `${pct}%`, bgcolor: featured ? CTA_FILL : CTA_EDGE,
                borderRadius: 999,
                '@keyframes bilBar': { from: { transform: 'scaleX(0)' }, to: { transform: 'scaleX(1)' } },
                transformOrigin: 'left',
                animation: 'bilBar .6s cubic-bezier(.2,.7,.3,1) both',
                animationDelay: `${(delay || 0) + 120}ms`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}
            />
          </Box>
        )}

        {/* lastsLabel é o campo que decide a compra — então ele vem antes do preço por mensagem,
            e some (vazio) em conta sem ritmo medido, em vez de fingir que sabe. */}
        {pkg.lastsLabel && (
          <Typography variant="body2" sx={{ color: CTA_INK, fontWeight: 'var(--fw-bold)', ...BREAK }}>
            {pkg.lastsLabel}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.25, sm: 1 }, columnGap: 1 }}>
          {pkg.perMsgLabel && (
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{pkg.perMsgLabel}</Typography>
          )}
          {pkg.validityLabel && (
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{pkg.validityLabel}</Typography>
          )}
        </Box>
      </Box>

      {/* preço e ação: coluna fixa à direita no desktop, bloco cheio no celular */}
      <Box
        sx={{
          flex: '0 0 auto', width: { xs: '100%', sm: 190 },
          display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', sm: 'flex-end' }, gap: 1,
        }}
      >
        <Typography variant="h5" component="p" sx={{ ...HEAD, color: 'var(--text-primary)', textAlign: { xs: 'left', sm: 'right' }, ...BREAK }}>
          {pkg.priceLabel}
        </Typography>
        <Button
          onClick={() => a.onBuyPackage?.(pkg.id)}
          disableElevation
          sx={{ ...(secondary ? CTA_OUTLINE_BTN_SX : CTA_BTN_SX), width: { xs: '100%', sm: 'auto' } }}
          aria-label={`Comprar ${pkg.qtyLabel} por ${pkg.priceLabel}`}
        >
          Comprar
        </Button>
      </Box>
    </Box>
  );
}

function PackageSkeletonRow() {
  return (
    <Box
      component="li"
      sx={{
        listStyle: 'none', borderRadius: 'var(--radius-card)', border: '1px solid',
        borderColor: 'var(--divider)', bgcolor: 'var(--surface)', p: { xs: 1.75, sm: 2 },
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2,
      }}
    >
      <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
        <Skeleton variant="text" width="45%" height={26} sx={{ bgcolor: 'var(--bg-page)' }} />
        <Skeleton variant="text" width="60%" sx={{ bgcolor: 'var(--bg-page)' }} />
        <Skeleton variant="rounded" height={6} sx={{ bgcolor: 'var(--bg-page)', my: 1 }} />
        <Skeleton variant="text" width="35%" sx={{ bgcolor: 'var(--bg-page)' }} />
      </Box>
      <Box sx={{ flex: '0 0 auto', width: { xs: '100%', sm: 190 }, display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', sm: 'flex-end' }, gap: 1 }}>
        <Skeleton variant="text" width={100} height={30} sx={{ bgcolor: 'var(--bg-page)' }} />
        <Skeleton variant="rounded" height={46} sx={{ bgcolor: 'var(--bg-page)', width: { xs: '100%', sm: 120 } }} />
      </Box>
    </Box>
  );
}

function StoreCard({ vm, a }) {
  const packages = vm.packages || [];
  const maxQty = packages.reduce((m, p) => (p.qty > m ? p.qty : m), 0);
  const pending = vm.pendingPurchase;
  const last = vm.lastPurchase;

  let body;
  if (vm.errorMessage) {
    body = (
      <StateBlock
        icon={<ErrorRounded sx={{ fontSize: 30, color: 'var(--color-error)' }} />}
        title="Não deu para carregar os pacotes"
        description={vm.errorMessage}
        action={
          <Button onClick={() => a.onRetry?.()} disableElevation startIcon={<ReplayRounded sx={{ fontSize: 20 }} />} sx={PRIMARY_BTN_SX}>
            Tentar de novo
          </Button>
        }
      />
    );
  } else if (vm.packagesLoading) {
    body = (
      <Box component="ul" aria-busy="true" aria-label="Carregando os pacotes" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <PackageSkeletonRow />
        <PackageSkeletonRow />
        <PackageSkeletonRow />
      </Box>
    );
  } else if (vm.packagesEmpty || packages.length === 0) {
    // Catálogo vazio é falha NOSSA (produto despublicado, medidor errado). O texto assume isso:
    // nada de "nada por aqui", que joga a culpa em quem só queria comprar.
    body = (
      <StateBlock
        icon={<CloudOffRounded sx={{ fontSize: 30, color: 'var(--color-warning)' }} />}
        title="Os pacotes não estão aparecendo — e o problema é nosso"
        description="A lista de pacotes não foi publicada direito do nosso lado. Fale com a gente que resolvemos e liberamos sua recarga na hora."
        action={
          <Button onClick={() => a.onContactSupport?.()} disableElevation startIcon={<SupportAgentRounded sx={{ fontSize: 20 }} />} sx={PRIMARY_BTN_SX}>
            Falar com o suporte
          </Button>
        }
      />
    );
  } else {
    body = (
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {packages.map((p, i) => (
          <PackageRow
            key={p.id}
            pkg={p}
            maxQty={maxQty}
            showBar={packages.length > 1}
            secondary={!!pending}
            a={a}
            delay={180 + i * 70}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ gridArea: 'loja', alignSelf: 'start', ...CARD_SX, p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2, ...rise(90) }}>
      <CardTitle
        icon={<ShoppingCartRounded sx={{ fontSize: 20 }} />}
        hint="Pagamento por PIX. Os créditos entram assim que o pagamento cair."
      >
        Recarregar seu saldo
      </CardTitle>

      {pending && <PendingPurchase p={pending} a={a} />}

      {last && !vm.errorMessage && !vm.packagesEmpty && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <HistoryRounded aria-hidden="true" sx={{ fontSize: 16, color: 'var(--text-secondary)', flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)', ...BREAK }}>
            Da última vez você levou o {last.packLabel} ({last.qtyLabel}) por {last.priceLabel}, {last.agoLabel}.
          </Typography>
        </Box>
      )}

      {body}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Tela
   ═══════════════════════════════════════════════════════════════════════════ */

export default function BillingView({ vm, actions }) {
  const a = actions || {};
  // `mockCarregando` traz saldo e catálogo carregando juntos e `usage.hasData: false`. Sem esta
  // leitura, o ritmo mostraria o vazio de CONTA NOVA durante o carregamento — dizer "ainda não dá
  // para medir" para quem tem 30 dias de histórico é o mesmo erro do "0 créditos" falso.
  const booting = !!vm.credits?.loading || !!vm.packagesLoading;
  // As áreas do grid são montadas só com os blocos que existem: uma área nomeada sem item vira
  // linha de altura zero, mas o `gap` continua contando e abriria um buraco no topo (sem aviso)
  // ou no pé (sem a saída de desligar).
  const showAck = !!vm.arrivedFromAlert;
  const showExit = !!vm.canDisableIntegration;
  const areas = (wide) => [
    showAck && (wide ? '"ack ack"' : '"ack"'),
    wide ? '"saldo loja"' : '"saldo"',
    wide ? '"ritmo loja"' : '"loja"',
    !wide && '"ritmo"',
    showExit && (wide ? '"saida saida"' : '"saida"'),
  ].filter(Boolean).join(' ');

  return (
    <Box sx={{ color: 'var(--text-primary)' }}>
      {/* Moldura da tela. O título é da ÁREA (Compras), não do produto: a mesma rota vai receber
          os outros produtos vendáveis. O produto é o ESCOPO, e aparece como pastilha ao lado —
          é o `?product=whatsapp` visível, e é onde entram os irmãos dele depois. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 'var(--space-3)' }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
            <Typography variant="h1" component="h1" sx={{ color: 'var(--text-primary)', textWrap: 'pretty' }}>
              Compras
            </Typography>
            <Box
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.75, maxWidth: '100%',
                bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)',
                borderRadius: 999, px: 1.25, py: '4px',
              }}
            >
              <ChatBubbleRounded aria-hidden="true" sx={{ fontSize: 14, color: 'var(--text-secondary)' }} />
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)' }}>
                {vm.productLabel}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5, textWrap: 'pretty' }}>
            Seu saldo, seu consumo e os pacotes disponíveis.
          </Typography>
        </Box>
      </Box>

      {/* DUAS COLUNAS a partir de lg (o pane do Admin é o viewport menos o drawer de 276px e o
          padding de 48px: em 1600px sobram ~1276px, e empilhar tudo num filete de 800 deixaria
          meio monitor vazio). A ORDEM DO DOM é a do celular — reconhecimento, saldo, LOJA, ritmo
          — e as áreas remontam isso em duas colunas no desktop, com a loja ocupando a mais larga
          e as duas linhas de altura da coluna de estado. */}
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 'var(--space-2)', md: 'var(--space-3)' },
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(300px, 0.85fr) minmax(0, 1.15fr)' },
          gridTemplateAreas: { xs: areas(false), lg: areas(true) },
        }}
      >
        {showAck && <AlertAck vm={vm} />}
        <BalanceCard vm={vm} />
        <StoreCard vm={vm} a={a} />
        <UsageCard vm={vm} loading={booting} />

        {/* A saída de quem não quer mais o recurso. Existe porque é honesto oferecer, e é RODAPÉ:
            texto secundário e botão quieto, nunca um segundo botão do peso do comprar. */}
        {showExit && (
          <Box
            sx={{
              gridArea: 'saida',
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center',
              gap: 0.5, pt: 1, borderTop: '1px solid', borderColor: 'var(--divider)',
            }}
          >
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
              Não quer mais avisar seus clientes pelo WhatsApp?
            </Typography>
            <Button
              onClick={() => a.onGoToIntegrations?.()}
              startIcon={<PowerSettingsNewRounded sx={{ fontSize: 16 }} />}
              sx={{ ...QUIET_BTN_SX, textDecoration: 'underline', textUnderlineOffset: '3px' }}
            >
              Desligar nas Integrações
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
